import { FunctionComponent, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import useNonInitialEffect from '@versiondos/hooks';
import Spinner from 'components/UI/Spinner';
import DaycareOnboarding from './DaycareOnboarding';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { DaycareForm } from './Form/DaycareForm';
import DaycareServicesTable from './List/DaycareServicesTable';
import { Service, isDaycareService } from 'model/Service';
import { useAuth, useDaycareServices, DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from 'hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import styles from './DaycareComponent.module.scss';
import { resetServicesThunk } from 'actions/services/ServicesActions';
import Button from 'components/UI/V3/Button/Button';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';

const DaycareComponent: FunctionComponent = () => {
    const {
        services: daycareServices,
        savedService,
        meta,
        fetchDaycareServices,
        duplicateDaycareService,
        deleteDaycareService,
        toggleDaycareServiceStatus
    } = useDaycareServices();

    const dispatch = useDispatch();
    const auth = useAuth();

    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const [loading, setLoading] = useState({ services: false, initialLoading: true });
    const [selectedOption, setSelectedOption] = useState('active');
    const [searchText, setSearchText] = useState('');
    const [marketplaceIds, setMarketplaceIds] = useState<Array<number>>(() =>
        auth.marketplaceId ? [auth.marketplaceId] : []
    );
    const [pageInfo, setPageInfo] = useState({
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE
    });
    const [showAddDaycare, setShowAddDaycare] = useState(false);
    const [editingService, setEditingService] = useState<Service | null>(null);
    const [lastSavedServiceId, setLastSavedServiceId] = useState<number | undefined>();
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const fetchServices = () => {
        setLoading(prev => ({ ...prev, services: true }));
        fetchDaycareServices(pageInfo.page, pageInfo.pageSize, selectedOption, searchText, marketplaceIds);
    };

    useEffect(() => {
        dispatch(resetServicesThunk());
        fetchServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Only fetch if not in initial loading state
        if (!loading.initialLoading) {
            fetchServices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageInfo, selectedOption, searchText, marketplaceIds]);

    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.FetchSuccess:
            case ServicesStatus.Error:
                setLoading(prev => ({ services: false, initialLoading: false }));
                break;
            case ServicesStatus.SaveBulkSuccess:
                fetchServices();
                break;
        }
    }, [servicesStatus]);

    // POST-SAVE REFRESH (legacy single-service save)
    useEffect(() => {
        if (savedService && isDaycareService(savedService)) {
            // Only fetch fresh data if this is a new save
            if (savedService.id && savedService.id !== lastSavedServiceId) {
                setLastSavedServiceId(savedService.id);
                fetchServices();
            }
        }
    }, [savedService]);

    // HANDLERS with immediate loading control (prevents visual flashes)
    const handlePageChange = (page: number) => {
        setLoading(prev => ({ ...prev, services: true }));
        setPageInfo(prev => ({ ...prev, page }));
    };

    const handlePageSizeChange = (size: number) => {
        setLoading(prev => ({ ...prev, services: true }));
        setPageInfo({ page: 1, pageSize: size });
    };

    const handleStatusChange = (option: string) => {
        setLoading(prev => ({ ...prev, services: true }));
        setSelectedOption(option);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleSearchChange = (text: string) => {
        setLoading(prev => ({ ...prev, services: true }));
        setSearchText(text);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleMarketplaceIdsChange = (ids: Array<number>) => {
        setLoading(prev => ({ ...prev, services: true }));
        setMarketplaceIds(ids);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleEditService = (service: Service) => {
        console.log('🔧 Edit service:', service);
        setEditingService(service);
        setShowAddDaycare(true);
    };

    const handleDuplicateService = (service: Service) => {
        duplicateDaycareService(service);
    };

    const handleDeactivateService = (service: Service) => {
        toggleDaycareServiceStatus(service, {
            page: pageInfo.page,
            per: pageInfo.pageSize,
            status: selectedOption,
            searchText: searchText,
            marketplaceIds
        });
    };

    const handleCloseModal = () => {
        setShowAddDaycare(false);
        setEditingService(null);
    };

    const headerRightContent = (showManageRooms: boolean) => (
        <div className={styles.headerRightContent}>
            {showManageRooms && (
                <a
                    href="/business_settings/info/rooms"
                    className={styles.manageRooms}
                >
                    Manage Rooms
                </a>
            )}
            <OrganizationMarketplacesFilter
                selected={marketplaceIds}
                singleSelection
                onChangeSelection={handleMarketplaceIdsChange}
                className={styles.locationsFilter}
            />
            <Button
                leftIcon={faPlus}
                label={'Add Service'}
                onClick={() => setShowAddDaycare(true)}
                id="daycare_add_button"
            />
        </div>
    );

    const renderContent = () => {
        // Initial loading
        if (loading.initialLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        // Business has no daycare services (with default filters)
        if (
            daycareServices.length === 0 &&
            meta?.totalServices === 0 &&
            selectedOption === 'active' &&
            !searchText &&
            !loading.services
        ) {
            return (
                <DaycareOnboarding
                    onAddService={() => setShowAddDaycare(true)}
                    headerRightContent={headerRightContent(false)}
                />
            );
        }

        // Business has daycare services → show full layout
        return (
            <div className={styles.root}>
                {/* Header always visible when there's service context */}
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={'Daycare Services'}
                        buttonLabel={isMobile ? 'Add' : 'Add Service'}
                        displayButton={true}
                        onClick={() => setShowAddDaycare(true)}
                        id="daycare_add_button"
                        className={styles.headerContainer}
                        customButton={headerRightContent(true)}
                    />

                    <SelectionBar
                        options={[
                            { id: 'active', name: 'Active' },
                            { id: 'inactive', name: 'Inactive' },
                            { id: 'all', name: 'All' }
                        ]}
                        selectedId={selectedOption}
                        onSelect={handleStatusChange}
                        className={styles.selectionBar}
                    />

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={handleSearchChange}
                        placeholder="Search by service name"
                        className={styles.searchBarContainer}
                    />
                </div>

                {/* Table handles: loading, error, filtered empty, and data */}
                <div className={styles.tableContainer}>
                    <DaycareServicesTable
                        services={daycareServices}
                        meta={meta}
                        isLoading={loading.services}
                        isEmpty={daycareServices.length === 0 && !loading.services}
                        onEdit={handleEditService}
                        onDuplicate={handleDuplicateService}
                        onDeactivate={handleDeactivateService}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onRetry={() => fetchServices()}
                    />
                </div>
            </div>
        );
    };

    return (
        <>
            {renderContent()}
            {showAddDaycare && (
                <DaycareForm
                    mode={editingService ? 'edit' : 'add'}
                    service={editingService || undefined}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
};

export default DaycareComponent;
